/**
 * Client for uploading design project media and notes via REST API.
 * Uses apiClient (axios) for all requests.
 */
import { MediaSource } from "@yardzen/api-dtos";
import { Media, R2Target } from "@yardzen/media";
import { validateFileSize } from "@yardzen/media/client";
import axios from "axios";
import { apiClient } from "../base-client";

export async function uploadPhotos(
  files: File[],
  designProjectId: string,
  source: MediaSource,
  tags?: string[],
  // Opt-in R2 storage (e.g. the Toll fallback uploads). Absent ⇒ GCS as before.
  r2Target?: R2Target,
  // Sub-path segments under the project folder for the stored object key, e.g.
  // ["home-photos"] or ["site-plan"]. The server prepends designProjectId.
  objectPath?: string[],
) {
  const uploaded = [];
  for (const file of files) {
    const fileTooLargeError = validateFileSize(file);
    if (fileTooLargeError) {
      uploaded.push({
        error: fileTooLargeError,
        file,
      });
      continue;
    }
    // 1. Get signed upload URL and mediaId
    const res = await apiClient.post(
      `/design-projects/${designProjectId}/media`,
      {
        mimeType: file.type,
        tags,
        source,
        objectName: file.name,
        // R2 presigned PUTs sign ContentLength, so always send the size.
        fileSize: file.size,
        ...(r2Target ? { r2Target } : {}),
        ...(objectPath?.length ? { objectPath } : {}),
      },
    );
    const { mediaId, writeUrl } = res.data;

    // 2. Upload file to signed URL
    const uploadRes = await axios.put(writeUrl, file, {
      headers: { "Content-Type": file.type },
    });
    if (uploadRes.status < 200 || uploadRes.status >= 300)
      throw new Error("Failed to upload file");

    uploaded.push({ mediaId, file });
  }
  return uploaded;
}

export async function deletePhoto(mediaId: string) {
  const res = await apiClient.delete(`/design-projects/media/${mediaId}`);
  if (res.status < 200 || res.status >= 300)
    throw new Error("Failed to delete photo");
}

export async function saveNote(mediaId: string, note: string) {
  const res = await apiClient.patch(`/design-projects/media/${mediaId}/notes`, {
    notes: note,
  });
  if (res.status < 200 || res.status >= 300)
    throw new Error("Failed to save note");
}

export async function getDesignProjectMedia(
  designProjectId: string,
  source: MediaSource,
): Promise<Media[]> {
  const params: Record<string, any> = { source };

  const res = await apiClient.get(`/design-projects/${designProjectId}/media`, {
    params,
  });
  return res.data as Media[];
}
